import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Rnd } from 'react-rnd'
import { Box, IconButton } from '@mui/material'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'

/**
 * collageItems = [
 *   {
 *     imageUrl: string,
 *     x: number,
 *     y: number,
 *     width: number,
 *     height: number,
 *     rotation: number
 *   }
 * ]
 *
 * setCollageItems: function => kolaj ögelerini günceller
 * 
 * Bileşeni html2canvas ile "DOM" olarak yakalamak için
 * parent’tan gelen ref'i en dış <Box> elementine vereceğiz.
 */
const CollageCanvas = forwardRef(function CollageCanvas({ collageItems, setCollageItems }, ref) {
  const rootRef = useRef(null)
  const [activeIndex, setActiveIndex] = React.useState(null)

  useImperativeHandle(ref, () => {
    const node = rootRef.current
    if (!node) return null
    node.clearSelection = () => setActiveIndex(null)
    return node
  })

  // Döndürme butonları
  const handleRotate = (index, delta) => {
    setCollageItems((prev) => {
      const newArr = [...prev]
      const currentRotation = newArr[index].rotation || 0
      newArr[index].rotation = currentRotation + delta
      return newArr
    })
  }

  // Sürükleme bitince pozisyon güncelle
  const handleDragStop = (index, d) => {
    setCollageItems((prev) => {
      const newArr = [...prev]
      newArr[index].x = d.x
      newArr[index].y = d.y
      return newArr
    })
  }

  // Yeniden boyutlandırma bitince ölçüler + konum güncelle
  const handleResizeStop = (index, refEl, position) => {
    setCollageItems((prev) => {
      const newArr = [...prev]
      newArr[index].width = parseFloat(refEl.style.width)
      newArr[index].height = parseFloat(refEl.style.height)
      newArr[index].x = position.x
      newArr[index].y = position.y
      return newArr
    })
  }

  return (
    <Box
      ref={rootRef}
      onClick={() => setActiveIndex(null)}
      sx={{
        width: '100%',
        maxWidth: 480,
        mx: 'auto',
        // Sosyal medya post oranına yakın (4:5 / 3:4)
        aspectRatio: '4 / 5',
        minHeight: { xs: 320, sm: 400 },
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid rgba(15,23,42,0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 18px 45px rgba(15,23,42,0.55)'
      }}
    >
      {collageItems.map((item, idx) => (
        <Rnd
          key={idx}
          onClick={(e) => { e.stopPropagation(); setActiveIndex(idx) }}
          onMouseDown={() => setActiveIndex(idx)}
          onTouchStart={() => setActiveIndex(idx)}
          position={{ x: item.x, y: item.y }}
          size={{
            width: item.width || 100,
            height: item.height || 100
          }}
          onDragStop={(e, d) => handleDragStop(idx, d)}
          onResizeStop={(e, direction, refEl, delta, position) =>
            handleResizeStop(idx, refEl, position)
          }
          bounds="parent"
          cancel=".no-drag"
          enableResizing={{
            topLeft: true,
            topRight: true,
            bottomLeft: true,
            bottomRight: true
          }}
          resizeHandleStyles={activeIndex === idx ? {
            bottomRight: {
              width: 14,
              height: 14,
              borderRadius: '999px',
              border: '1px solid rgba(15,23,42,0.3)',
              backgroundColor: '#f9fafb',
              position: 'absolute',
              right: -7,
              bottom: -7,
              boxShadow: '0 2px 6px rgba(15,23,42,0.25)'
            },
            bottomLeft: {
              width: 14,
              height: 14,
              borderRadius: '999px',
              border: '1px solid rgba(15,23,42,0.3)',
              backgroundColor: '#f9fafb',
              position: 'absolute',
              left: -7,
              bottom: -7,
              boxShadow: '0 2px 6px rgba(15,23,42,0.25)'
            },
            topRight: {
              width: 14,
              height: 14,
              borderRadius: '999px',
              border: '1px solid rgba(15,23,42,0.3)',
              backgroundColor: '#f9fafb',
              position: 'absolute',
              right: -7,
              top: -7,
              boxShadow: '0 2px 6px rgba(15,23,42,0.25)'
            },
            topLeft: {
              width: 14,
              height: 14,
              borderRadius: '999px',
              border: '1px solid rgba(15,23,42,0.3)',
              backgroundColor: '#f9fafb',
              position: 'absolute',
              left: -7,
              top: -7,
              boxShadow: '0 2px 6px rgba(15,23,42,0.25)'
            }
          } : {}}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${item.imageUrl})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'transparent',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              transform: `rotate(${item.rotation || 0}deg)`,
              transformOrigin: 'center center',
            }}
          >
            {/* Döndürme butonları (isteğe bağlı) */}
            {activeIndex === idx && (
              <>
                <IconButton
                  className="no-drag collage-control"
                  size="small"
                  onClick={() => handleRotate(idx, -15)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    width: 28,
                    height: 28,
                    p: 0,
                    color: '#0f172a',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    borderRadius: '999px',
                    boxShadow: '0 4px 10px rgba(15,23,42,0.25)',
                    '&:hover': { bgcolor: '#e5e7eb' }
                  }}
                >
                  <RotateLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  className="no-drag collage-control"
                  size="small"
                  onClick={() => handleRotate(idx, 15)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 28,
                    height: 28,
                    p: 0,
                    color: '#0f172a',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    borderRadius: '999px',
                    boxShadow: '0 4px 10px rgba(15,23,42,0.25)',
                    '&:hover': { bgcolor: '#e5e7eb' }
                  }}
                >
                  <RotateRightIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Rnd>
      ))}
    </Box>
  )
})

export default CollageCanvas